import { useState } from 'react';

import { PlusIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { Button } from '@nesso/ui/primitives/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@nesso/ui/primitives/collapsible';

import { type AddClientContactInput, MAX_INPUT_FIELD_LENGTH } from '@/entities/client';
import { UsAddressFields } from '@/shared/ui/forms/us-address-fields';

type Props = {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    isLocked?: boolean;
};

// Reveal-and-stay, like the client profile's "Apt, Suite, Floor": once opened the trigger is gone,
// so a half-typed ZIP can never be tucked out of sight while it still blocks the save (FR-019).
// Opening it makes nothing required (FR-010); the fields stay in the form either way, so an
// untouched disclosure submits an all-empty address, which the server stores as no address.
// The open state is the caller's so it survives a tab switch, and so a selected person's saved
// address can open it.
export function AddClientContactFormAddress({ isOpen, onOpenChange, isLocked = false }: Props) {
    const {
        formState: { isSubmitting },
    } = useFormContext<AddClientContactInput>();
    // Focus follows the therapist's own click only: a disclosure that arrives open must not take
    // focus from the tab or the result that was just chosen.
    const [wasOpenedByClick, setWasOpenedByClick] = useState(false);

    return (
        <Collapsible open={isOpen} onOpenChange={onOpenChange}>
            {!isOpen && (
                <CollapsibleTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={isSubmitting}
                        onClick={() => setWasOpenedByClick(true)}
                        className="-ml-3"
                    >
                        <PlusIcon className="w-4 h-4" /> Address
                    </Button>
                </CollapsibleTrigger>
            )}
            <CollapsibleContent className="flex flex-col gap-8">
                <UsAddressFields
                    maxLength={MAX_INPUT_FIELD_LENGTH}
                    autoFocus={wasOpenedByClick}
                    disabled={isLocked}
                />
            </CollapsibleContent>
        </Collapsible>
    );
}
